import { DestroyRef, inject, Injectable, InjectionToken, NgZone } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { App } from '@capacitor/app';
import { PushNotifications } from '@capacitor/push-notifications';
import { Badge } from '@capawesome/capacitor-badge';
import { EMPTY, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';
import { HeaderService } from 'src/app/layout/services/header.service';
import { PlatformService } from 'src/app/shared/services/platform.service';
import { selectUser } from 'src/app/state/selectors/user.selectors';
import { updateUserData } from 'src/app/state/actions/user.action';
import { LocalStorageService } from './local-storage.service';

export interface NativeBadgePlugins {
  badge: Pick<typeof Badge, 'checkPermissions' | 'set'>;
  pushNotifications: Pick<typeof PushNotifications, 'removeAllDeliveredNotifications' | 'addListener'>;
  app: Pick<typeof App, 'addListener'>;
}

/** Plugins Capacitor via DI — os proxies do Capacitor não são espionáveis nos testes. */
export const NATIVE_BADGE_PLUGINS = new InjectionToken<NativeBadgePlugins>('NATIVE_BADGE_PLUGINS', {
  providedIn: 'root',
  factory: () => ({ badge: Badge, pushNotifications: PushNotifications, app: App }),
});

/**
 * Mantém o badge do ícone do app nativo igual ao contador do sino
 * (`notificationData.newNotifications` no store, vindo do server).
 *
 * - O push só *define* o badge no momento da entrega (iOS: `aps.badge`); ler in-app
 *   não gera push, então é o app que precisa zerar/atualizar o número.
 * - Ao voltar do background ou receber push com o app aberto, re-busca o contador no
 *   server — o store só é carregado na abertura do app e ficaria defasado.
 * - Contador zerado tira os avisos do SmartApp da bandeja: no Android é isso que zera
 *   o badge do launcher (calculado das notificações ativas); no iOS o badge 0 já o faz.
 */
@Injectable({ providedIn: 'root' })
export class NativeBadgeService {
  private readonly store = inject(Store);
  private readonly headerService = inject(HeaderService);
  private readonly platformService = inject(PlatformService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly plugins = inject(NATIVE_BADGE_PLUGINS);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  private readonly refresh$ = new Subject<void>();
  private initialized = false;

  /** Liga a sincronização — chamado uma vez no AppComponent. No web não faz nada. */
  init(): void {
    if (!this.platformService.isNativeApp || this.initialized) return;
    this.initialized = true;

    this.store
      .select(selectUser)
      .pipe(
        map((user) => user?.notificationData?.newNotifications),
        filter((count): count is number => typeof count === 'number'),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((count) => this.apply(count));

    this.refresh$
      .pipe(
        // Só com sessão e com o store já hidratado (getDados) — evita GET sem login.
        switchMap(() => this.store.select(selectUser).pipe(take(1))),
        filter((user) => !!user?.notificationData && !!this.localStorageService.getToken()),
        switchMap(() => this.headerService.getNotificationData().pipe(catchError(() => EMPTY))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((notificationData) => this.store.dispatch(updateUserData({ notificationData })));

    // Listeners do Capacitor rodam fora da zona do Angular.
    const refresh = () => this.zone.run(() => this.refresh$.next());
    this.plugins.app.addListener('resume', refresh);
    this.plugins.pushNotifications.addListener('pushNotificationReceived', refresh);
  }

  private async apply(count: number): Promise<void> {
    try {
      if (this.platformService.isIOSApp) {
        // `Badge.set` pede autorização de badge ao iOS antes de gravar: com a permissão
        // ainda não decidida, abriria o prompt do sistema (só badge, sem alerta/som) fora
        // do aviso de push do app. Só mexe no badge com a permissão já concedida.
        const { display } = await this.plugins.badge.checkPermissions();
        if (display !== 'granted') return;
        await this.plugins.badge.set({ count });
      }
      if (count === 0) {
        await this.plugins.pushNotifications.removeAllDeliveredNotifications();
      }
    } catch {
      // Build nativo sem o plugin (ou erro do SO): o badge fica como está.
    }
  }
}
